import { cleanAddressBar, isNative } from "../platform";
import { ChannelInfo, StatusComposer, StatusViewer } from "./Updates";
import { BroadcastInfo } from "./Communities";
import { CallOverlay, joinCall } from "./Call";
import { call, groupCalls } from "../calls";
import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import { isPrimary, switchAccount, type Session } from "../api";
import { Engine } from "../engine";
import * as S from "../state";
import { ChatView } from "./ChatView";
import { askNotifications, Sidebar } from "./Sidebar";
import { ContactInfo } from "./Panels";
import { GroupInfo, JoinDialog } from "./Groups";
import { Icon } from "./Icon";
import { CommandPalette, palette, shortcutsHelp, ShortcutsHelp, useShortcuts } from "./Shortcuts";
import { LockDialog, lockDialog } from "./ChatLock";
import { setBadge } from "../pwa";
import { LinkCodePrompt } from "./Link";

/** The running engine for the logged-in account. */
export const engine = signal<Engine | null>(null);

// The chat list is drawn from the local store before the engine has started:
// a tap in that window opens the chat as soon as it's ready instead of being lost.
let pendingOpen: string | null = null;
export function openChat(jid: string) {
  if (engine.value) void engine.value.openChat(jid);
  else pendingOpen = jid;
}
// Tests only: reach the engine from puppeteer (localStorage "xmm.test" = "1").
try {
  if (localStorage.getItem("xmm.test") === "1") (window as unknown as { __xmm: unknown }).__xmm = { engine };
} catch {
  /* storage blocked */
}
/** Left drawer (over the chat list) and right drawer (contact info). */
export const drawer = signal<"none" | "newChat" | "newGroup" | "profile" | "devices" | "starred" | "archived" | "privacy" | "backup" | "calls" | "statusPrivacy" | "channelDirectory" | "newChannel" | "newCommunity" | "community" | "newBroadcast" | "appearance" | "storage">("none");
export const infoOpen = signal(false);

export function Main({ session }: { session: Session }) {
  useShortcuts();
  useEffect(() => {
    let e: Engine | null = null;
    let cancelled = false;
    void Engine.start(session).then((x) => {
      if (cancelled) void x.stop();
      else {
        engine.value = e = x;
        if (pendingOpen) void x.openChat(pendingOpen);
        pendingOpen = null;
      }
    });
    return () => {
      cancelled = true;
      engine.value = null;
      void e?.stop();
      resetState();
    };
    // Engine lifetime is tied to the device session, not token refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.session_id]);

  // Notification clicks (service worker) open the chat.
  useEffect(() => {
    const e = engine.value;
    if (!e) return;
    const params = new URLSearchParams(location.search);
    const want = params.get("chat") ?? params.get("channel");
    if (params.get("channel") && S.isChannel(want ?? "")) S.tab.value = "channels";
    // A notification for another account logged in here: switch to it first.
    const open = (jid: string, account?: string | null) => {
      if (account && account !== session.xmpp.jid) {
        pendingOpen = jid;
        switchAccount(account);
      } else void e.openChat(jid);
    };
    if (want) {
      cleanAddressBar();
      open(want, params.get("account"));
    }
    // The installed app's shortcuts (manifest): /?action=new-chat
    if (params.get("action") === "new-chat") {
      cleanAddressBar();
      drawer.value = "newChat";
    }
    // An event's call link (/?call=<chat>&media=video): open that chat and
    // join its call (or start it). Only for chats this account is in.
    const callChat = params.get("call");
    if (callChat) {
      cleanAddressBar();
      void joinCallLink(e, callChat, params.get("media") === "voice" ? "audio" : "video");
    }
    const onMsg = (ev: MessageEvent) => ev.data?.type === "open-chat" && ev.data.jid && open(ev.data.jid, ev.data.account);
    navigator.serviceWorker?.addEventListener("message", onMsg);
    return () => navigator.serviceWorker?.removeEventListener("message", onMsg);
  }, [engine.value]);

  // Browsers only show the permission prompt after a click or key press: ask
  // on the first one after the app opens (once per page load), like the
  // banner's button would.
  useEffect(() => {
    if (isNative || typeof Notification === "undefined" || Notification.permission !== "default" || !window.isSecureContext) return;
    const ask = () => {
      off();
      void askNotifications();
    };
    const off = () => {
      window.removeEventListener("pointerup", ask, true);
      window.removeEventListener("keyup", ask, true);
    };
    window.addEventListener("pointerup", ask, true);
    window.addEventListener("keyup", ask, true);
    return off;
  }, []);

  // Looking at a chat clears its notification (sw.js shows one per chat).
  const looking = S.attentive.value ? S.activeChat.value : null;
  useEffect(() => {
    if (!looking) return;
    void navigator.serviceWorker?.getRegistration().then((r) => r?.active?.postMessage({ type: "close", tag: looking })).catch(() => {});
  }, [looking]);

  // The phone app keeps its contacts in step with the address book (private
  // discovery; the names reach our other devices end-to-end encrypted).
  // It checks at start-up and every time the app comes back to the
  // foreground (a contact added meanwhile shows up right away).
  const online = S.conn.value === "online";
  useEffect(() => {
    const e = engine.value;
    if (!online || !isPrimary() || !e) return;
    const check = () =>
      void e.maybeSyncContacts().then((r) => {
        const n = r?.addedJids.length ?? 0;
        if (n) toast(n === 1 ? `${S.displayName(r!.addedJids[0])} is on xmm` : `${S.displayName(r!.addedJids[0])} and ${n - 1} more of your contacts are on xmm`);
      });
    check();
    let t = 0;
    const onVisible = () => {
      clearTimeout(t);
      if (document.visibilityState === "visible") t = window.setTimeout(check, 800);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => (clearTimeout(t), document.removeEventListener("visibilitychange", onVisible));
  }, [online, engine.value]);

  // Browser tab title shows unread chats, like web.whatsapp.com.
  const unread = S.totalUnread.value;
  useEffect(() => {
    document.title = unread ? `(${unread}) xmm` : "xmm";
    setBadge(unread); // the installed app's icon
  }, [unread]);

  // Invite links look like /join/<code>; they survive the login screen.
  const [joinCode, setJoinCode] = useState(() => /^\/join\/([\w-]{8,64})$/.exec(location.pathname)?.[1] ?? null);
  const closeJoin = () => {
    setJoinCode(null);
    cleanAddressBar();
  };

  const active = S.activeChat.value;
  return (
    <div class={"app" + (active ? " has-chat" : "") + (infoOpen.value && active ? " has-info" : "")}>
      <Sidebar session={session} />
      <main class="pane">{active ? <ChatView key={active} jid={active} /> : <Welcome />}</main>
      {infoOpen.value &&
        active &&
        (S.isGroup(active) ? (
          <GroupInfo jid={active} />
        ) : S.isChannel(active) ? (
          <ChannelInfo jid={active} />
        ) : S.isBroadcast(active) ? (
          <BroadcastInfo jid={active} />
        ) : (
          <ContactInfo jid={active} />
        ))}
      <StatusViewer />
      <StatusComposer />
      {joinCode && engine.value && <JoinDialog code={joinCode} onClose={closeJoin} />}
      <CallOverlay />
      {palette.value && <CommandPalette />}
      {lockDialog.value && <LockDialog />}
      <LinkCodePrompt />
      {shortcutsHelp.value && <ShortcutsHelp />}
      <Toasts />
    </div>
  );
}

/** Right after the app opens, the chat list and the group's ongoing call
 * (learned from the chat's recent call messages) may still be arriving:
 * wait a little for both, so the link joins that call instead of starting
 * another one. */
async function joinCallLink(e: Engine, chat: string, media: "audio" | "video") {
  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
  for (let i = 0; i < 20 && !S.chats.value.some((c) => c.jid === chat); i++) await sleep(500);
  if (!S.chats.value.some((c) => c.jid === chat)) return toast("This call link is for a chat you're not in");
  await e.openChat(chat);
  if (S.isGroup(chat)) for (let i = 0; i < 8 && !groupCalls.value.has(chat) && call.value?.phase !== "incoming"; i++) await sleep(500);
  joinCall(chat, media);
}

function Welcome() {
  return (
    <div class="welcome">
      <img src="/icon.svg" alt="" width={96} height={96} />
      <h1>xmm for web</h1>
      <p>Send and receive messages from any of your linked devices. Your chats are stored on this device and synced from the server.</p>
      <p class="muted small">
        <Icon name="lock" size={14} /> Your personal messages are delivered over an authenticated, encrypted connection.
      </p>
    </div>
  );
}

function Toasts() {
  const [items, setItems] = useState<{ id: number; text: string }[]>([]);
  useEffect(() => {
    const on = (e: Event) => {
      const id = Date.now() + Math.random();
      setItems((l) => [...l, { id, text: String((e as CustomEvent).detail) }]);
      setTimeout(() => setItems((l) => l.filter((x) => x.id !== id)), 4000);
    };
    window.addEventListener("xmm-toast", on);
    return () => window.removeEventListener("xmm-toast", on);
  }, []);
  return (
    <div class="toasts" role="status" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} class="toast">
          {t.text}
        </div>
      ))}
    </div>
  );
}

export function toast(text: string) {
  window.dispatchEvent(new CustomEvent("xmm-toast", { detail: text }));
}

function resetState() {
  S.chats.value = [];
  S.activeChat.value = null;
  S.messages.value = [];
  S.roster.value = new Map();
  S.presence.value = new Map();
  S.typing.value = new Map();
  S.lastSeen.value = new Map();
  S.profiles.value = new Map();
  S.groups.value = new Map();
  S.frequent.value = [];
  drawer.value = "none";
  infoOpen.value = false;
}
